/**
 * Per-bank positions-file layouts (Banks arc step 3b, migration 0114; backend
 * `routers/portfolio_layouts.py`, gated `portfolios.manage`). A layout says how one bank's
 * positions file is read: which heading fills each of the app's fields, which categories mean
 * cash and loan, the portfolio-number rule and short key, and the reference currency.
 */
import { api, ApiError } from './client';

export interface LayoutInput {
  /** field key → the file's heading (blank / absent = not in this bank's file) */
  columns: Record<string, string>;
  cash_categories: string[];
  loan_categories: string[];
  number_min_digits: number;
  number_max_digits: number;
  /** pad the number with zeros on the left to this length; null = keep it as given */
  pad_to: number | null;
  /** the short key = characters key_from..key_to of the number (1-based) */
  key_from: number;
  key_to: number;
  /** only for a file without a reference-currency column */
  ref_currency_fixed: string | null;
}
export interface Layout extends LayoutInput {
  id: string; bank_id: string; bank_mnemonic: string; updated_at: string; updated_by: string | null;
}
export interface LayoutBank {
  bank_id: string; mnemonic: string; name: string; has_layout: boolean;
  updated_at: string | null; updated_by: string | null;
}
export interface LayoutField { key: string; label: string; required: boolean; kind: 'text' | 'number' | 'date' }
export interface HeaderCheckItem { field: string; label: string; heading: string; required: boolean }
export interface HeaderCheck {
  ok: boolean; file_name: string;
  found: HeaderCheckItem[]; missing: HeaderCheckItem[]; repeated: HeaderCheckItem[];
  unused: string[]; problems: string[];
}

async function check(file: File, layout: LayoutInput): Promise<HeaderCheck> {
  const form = new FormData();
  form.append('file', file);
  form.append('layout', JSON.stringify(layout));
  const res = await fetch('/portfolio-layouts/check', { method: 'POST', credentials: 'include', body: form });
  if (!res.ok) {
    const j = await res.json().catch(() => null);
    throw new ApiError(res.status, (j && typeof j.detail === 'string' && j.detail) || 'The file could not be checked.');
  }
  return res.json();
}

export const portfolioLayoutsApi = {
  banks: () => api.get<LayoutBank[]>('/portfolio-layouts'),
  fields: () => api.get<{ fields: LayoutField[]; template: LayoutInput }>('/portfolio-layouts/fields'),
  get: (bankId: string) => api.get<Layout>(`/portfolio-layouts/${encodeURIComponent(bankId)}`),
  save: (bankId: string, layout: LayoutInput) => api.put<Layout>(`/portfolio-layouts/${encodeURIComponent(bankId)}`, layout),
  check,
};
